import 'react-vertical-timeline-component/style.min.css';

import RefreshIcon from '@mui/icons-material/Refresh';
import ReportIcon from '@mui/icons-material/Report';
import VideoFileIcon from '@mui/icons-material/VideoFile';
import { Box, Button, Typography } from '@mui/material';
import { cloneDeep } from 'lodash';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { VerticalTimeline, VerticalTimelineElement } from 'react-vertical-timeline-component';

import VnModalBody from '@/modals/components/VnModalBody';
import VnModalFooter from '@/modals/components/VnModalFooter';
import { useModalContext } from '@/modals/VNModalProvider';

import { AssetEventActionTypes, AssetEventType } from '../../enums';
import type { AssetEventAction } from '../../models/item';
import { TranscodeJobManagerService } from '../../services/transcode-job-manager-service';
import { applicationDataSelector } from '../../store/slices/application-data';
import useAppUtils from '../../utils/appUtils';
import { dateUtils } from '../../utils/dateUtils';

const { DateTime } = require('luxon');

interface IProps {
  assetAuditTrailsDetails: any;
  // onClose: () => void;
  // open: boolean;
}
const AssetTimeline = ({ assetAuditTrailsDetails }: IProps) => {
  const { ouData } = useSelector(applicationDataSelector);
  const { assetAuditTrails, asset } = assetAuditTrailsDetails;
  const { alert } = useAppUtils();
  const [auditTrail, setAuditTrail] = useState(assetAuditTrails);
  const { onClose } = useModalContext();

  const getTime = (audit, index) => {
    // console.log({audit, index});
    const lastIndex = assetAuditTrails.length - 1;
    if (index === lastIndex) {
      return '';
    }
    const nextEvent = assetAuditTrails[index + 1].timestamp; // todays date
    const currentEvent = audit.timestamp; // another date
    const duration = dateUtils.getDifferenceDurationByUnit(currentEvent, nextEvent);

    if (!duration) {
      return '';
    }
    const days = dateUtils.getDifferenceDurationByUnit(currentEvent, nextEvent, 'days');
    const hours = dateUtils.getDifferenceDurationByUnit(currentEvent, nextEvent, 'hours');
    const milliseconds = dateUtils.getDifferenceDurationByUnit(currentEvent, nextEvent, 'milliseconds');
    const minutes = dateUtils.getDifferenceDurationByUnit(currentEvent, nextEvent, 'minutes');
    const months = dateUtils.getDifferenceDurationByUnit(currentEvent, nextEvent, 'months');
    const seconds = dateUtils.getDifferenceDurationByUnit(currentEvent, nextEvent, 'seconds');
    const years = dateUtils.getDifferenceDurationByUnit(currentEvent, nextEvent, 'years');

    // console.log('duration', {days:days, hours, milliseconds, minutes, months, seconds, years})
    // if (years > 0) {
    //   return `${years} YEARS LATER`;
    // }
    if (months >= 1) {
      return `${Math.floor(months)} MONTHS LATER`;
    }
    if (days >= 1) {
      return `${days} DAYS LATER`;
    }
    if (hours >= 1) {
      return `${Math.floor(hours)} HOURS LATER`;
    }
    if (minutes >= 1) {
      return `${Math.floor(minutes)} MINUTES LATER`;
    }
    if (seconds >= 1) {
      return `${Math.floor(seconds)} SECONDS LATER`;
    }
    if (milliseconds > 0) {
      return `${Math.floor(milliseconds)} MILLI SECONDS LATER`;
    }
  };

  const onActionClick = async (action: AssetEventAction, auditIndex) => {
    if (action.actionType === AssetEventActionTypes.Restart) {
      try {
        const response = await TranscodeJobManagerService.restartJob(action.actionTargetId);
        const list = cloneDeep(auditTrail);
        list[auditIndex].restartRequestSubmitted = true;
        setAuditTrail(list);
        alert('Job has been started. Please allow some time for the status of the Asset to be updated.');
      } catch (ex) {
        alert(`Failed to retry asset job for '${asset.title}'.`, 'error');
      }
    }
  };
  const formatTitle = (type: AssetEventType) => {
    // console.log('type', type);
    let title = '';
    switch (type) {
      case AssetEventType.Created:
        title = 'Asset Created';
        break;
      case AssetEventType.Modified:
        title = 'Asset Modified';
        break;
      case AssetEventType.Deleted:
        title = 'Asset Deleted';
        break;
      case AssetEventType.IngestStarted:
        title = 'Asset Ingest Started';
        break;
      case AssetEventType.TranscodeJobStarted:
        title = 'Transcode Job Started';
        break;
      case AssetEventType.TranscodeJobEnded:
        title = 'Transcode Job Ended';
        break;
      case AssetEventType.LiveSegmentStarted:
        title = 'Live Segment Started';
        break;
      case AssetEventType.LiveSegmentEnded:
        title = 'Live Segment Ended';
        break;
      default:
        break;
    }
    return title;
  };

  const showRestartButton = (actionTargetId, index) => {
    const audit1 = auditTrail[index];
    const date1 = DateTime.fromISO(audit1.timestamp);

    const found = auditTrail.find((audit) => {
      const date2 = DateTime.fromISO(audit.timestamp);
      const isDate1BeforeDate2 = date1 < date2;
      return audit.actionTargetId === actionTargetId && isDate1BeforeDate2 && audit.hasError;
    });
    if (!found) {
      // Hide restart button if job is success for the next time
      const alreadyConformed = auditTrail.find((audit) => {
        const date2 = DateTime.fromISO(audit.timestamp);
        const isDate1BeforeDate2 = date1 < date2;
        return (
          !audit1.isJobSuccess &&
          isDate1BeforeDate2 &&
          audit.eventInfo.eventDescription === `'${audit1.jobType}' Transcode Job Completed Successfully!`
        );
      });
      if (alreadyConformed) {
        return false;
      }
      return true;
    }
    return false;
  };

  useEffect(() => { }, [auditTrail]);

  return (
    <>
      <VnModalBody>
        <Box sx={{ background: '#dedede' }}>
          <VerticalTimeline>
            {auditTrail.map((audit, index) => (
              <VerticalTimelineElement
                className="vertical-timeline-element--work"
                contentStyle={{ borderTop: audit.hasError ? '3px solid #d32f2f' : '3px solid #0db080' }}
                contentArrowStyle={{ borderRight: audit.hasError ? '7px solid #d32f2f' : '7px solid #0db080' }}
                date={dateUtils.format(audit.timestamp, ouData.timezone)}
                iconStyle={{ background: audit.hasError ? '#d32f2f' : '#0db080', color: '#fff' }}
                icon={audit.hasError ? <ReportIcon /> : <VideoFileIcon />}
                key={`audit-${audit.assetId}-${index}`}
              >
                <h2 className="vertical-timeline-element-title">{formatTitle(audit.eventInfo.eventType)}</h2>
                <Typography className="Mui-disabled" variant="caption" display="block">
                  {getTime(audit, index)}
                </Typography>

                <Typography>{audit.eventInfo.eventDescription}</Typography>
                <Box sx={{ mt: 1 }}>
                  {audit.eventInfo.actions.map((a, idx) => (
                    <React.Fragment key={`action-${a.actionTargetId}-${idx}`}>
                      {showRestartButton(a.actionTargetId, index) && (
                        <Button
                          size="small"
                          variant="outlined"
                          disabled={audit.restartRequestSubmitted}
                          color={audit.hasError ? 'error' : 'primary'}
                          onClick={() => onActionClick(a, index)}
                        >
                          {a.actionType} <RefreshIcon sx={{ color: audit.restartRequestSubmitted ? '' : 'red' }} />
                        </Button>
                      )}
                    </React.Fragment>
                  ))}
                </Box>
              </VerticalTimelineElement>
            ))}
          </VerticalTimeline>
        </Box>
      </VnModalBody>
      <VnModalFooter
        right={
          <Button autoFocus variant="contained" className="cancel-button" onClick={onClose}>
            Close
          </Button>
        }
      />
    </>
  );
};

export default AssetTimeline;
